import songService from '../service/song.service.js'
import playlistService from "../service/playlist.service.js";
import dbService from "../service/db.service.js";
import { createStore } from "vuex";

import userStore from "./user.store.js";

let latestAbortController = null;
const abortSupersededRequest = () => {
    if (latestAbortController) {
        latestAbortController.abort();
    }
    latestAbortController = new AbortController();
    return latestAbortController.signal;
};

// Guarda anti-disparo-duplo: o mesmo gesto de swipe é observado por até 4
// handlers (useSwipe em AppLayout/AppLayoutV2/TriadSongContainer + pointer
// handler no AppLayout). Sem isso, um swipe dispara N "next"/"previous" e
// pula de n para n+y. Só uma navegação por janela é aceita; tentativas nos
// limites da playlist (sem avanço real) não consomem a janela.
let lastSongNavAt = 0;
const SONG_NAV_DEBOUNCE_MS = 500;

const canNavigateSong = (songs, position, dir) => {
    if (!songs || songs.length === 0) return false;
    if (dir > 0 && position + 1 >= songs.length) return false;
    if (dir < 0 && position <= 0) return false;
    const now = Date.now();
    if (now - lastSongNavAt < SONG_NAV_DEBOUNCE_MS) return false;
    lastSongNavAt = now;
    return true;
};

function prefetchPlaylistSongs(playlistId, songs) {
    if (!songs || !Array.isArray(songs) || typeof window === 'undefined') return;
    const prefetch = () => {
        songs.slice(0, 50).forEach((song) => {
            const sId = song.data?.id || song.data?._id || song.songID || song.song || song.id || song._id;
            if (sId && playlistId) {
                fetch(`/api/playlist/${playlistId}/song/${sId}`).catch(() => {});
            }
            const rawSongId = song.song_id || song.songID || song.song;
            if (rawSongId) {
                fetch(`/api/song/${rawSongId}`).catch(() => {});
            }
        });
    };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetch, { timeout: 4000 });
    } else {
        setTimeout(prefetch, 500);
    }
}

const store = createStore({
    state: {
        playlistsOwnedBy: [],
        listPlaylists: [],
        playlist: null,
        currentSongPosition: 0,
        changedSong: false,
        currentSong: null,
        forceUpdate: false,
        // Contador monotônico para ordenar respostas de fetch/persist concorrentes
        // (loadSong force + transpose): o watcher só adota a mais recente por música.
        songFetchEpoch: 0,
        loading: false,
        loadingOwnedBy: false,
        error: false,
        message: "",
        status: 200
    },
    mutations: {
        UPDATE_CURRENT_SONG(state) {
            if (state.changedSong){
                if (state.playlist && state.playlist.songs && state.playlist.songs.length){
                    state.currentSong = state.playlist.songs[state.currentSongPosition]
                } else {
                    state.currentSong = null
                }
            }
            const hasLyrics = state.currentSong && (state.currentSong.processed || state.currentSong.modifiedLyric || state.currentSong.data?.content || state.currentSong.data?.lyric);
            state.loading = !hasLyrics;
            //TODO: Atualizar route
        },
        UPDATE_SONG(state, song) {
            if (state.playlist && state.playlist.songs && state.playlist.songs.length && song){
                let songIdx = state.playlist.songs.findIndex( (s) => {
                    const sSongId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id;
                    const songId = song.song_id || song.songID || song.song || song.data?.id || song.data?._id;
                    const sId = s.id || s._id;
                    const songPSId = song.id || song._id;
                    if (sSongId && songId && sSongId === songId) return true;
                    if (sId && songPSId && sId === songPSId) return true;
                    return false;
                });
                if (songIdx > -1) {
                    state.playlist.songs[songIdx] = song;
                    state.currentSongPosition = songIdx;
                    state.currentSong = song;

                    const playlistId = state.playlist.id || state.playlist._id;
                    const sId = song.song_id || song.songID || song.song || song.data?.id || song.data?._id || song.id;
                    if (playlistId && window.cacheInstance) {
                        const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", playlistId);
                        window.cacheInstance.setItem(playlistKey, state.playlist, playlistService.ttl);
                        if (sId) {
                            const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY.replace("[id]", playlistId).replace("[songId]", sId);
                            window.cacheInstance.setItem(songKey, song, playlistService.ttl);
                        }
                    }
                }
            } else {
                console.warn("Not Found")
            }
        },
        UPDATE(state, data) {
            if (data && data.error) {
                state.error = data.error;
                state.message = data.message;
                state.status = data.status;
            } else {
                const prevId = state.playlist?.id || state.playlist?._id;
                const newId = data?.id || data?._id;
                if (!prevId || !newId || String(prevId) !== String(newId)) {
                    state.currentSong = null;
                    state.currentSongPosition = 0;
                    state.changedSong = false;
                } else if (state.currentSong && data && Array.isArray(data.songs)) {
                    const currSongId = state.currentSong.song_id || state.currentSong.songID || state.currentSong.song || state.currentSong.data?.id || state.currentSong.data?._id || state.currentSong.id;
                    const matchingIdx = data.songs.findIndex(s => {
                        const sId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id || s.id;
                        return currSongId && sId && String(currSongId) === String(sId);
                    });
                    if (matchingIdx > -1) {
                        state.currentSongPosition = matchingIdx;
                        const freshSong = data.songs[matchingIdx];
                        const mergedSong = {
                            ...freshSong,
                            ...(state.currentSong.processed || state.currentSong.modifiedLyric ? {
                                processed: state.currentSong.processed ?? freshSong.processed,
                                modifiedLyric: state.currentSong.modifiedLyric || freshSong.modifiedLyric,
                                chord: state.currentSong.chord !== undefined ? state.currentSong.chord : freshSong.chord,
                                configuration: state.currentSong.configuration || freshSong.configuration,
                                data: {
                                    ...(freshSong.data || {}),
                                    ...(state.currentSong.data || {}),
                                    title: freshSong.data?.title || state.currentSong.data?.title || '',
                                    artist: freshSong.data?.artist || state.currentSong.data?.artist || null,
                                }
                            } : {})
                        };
                        data.songs[matchingIdx] = mergedSong;
                        state.currentSong = mergedSong;
                    }
                }
                state.playlist = data;
                state.error = false;
                state.message = "";
                state.status = 200;
            }
        },
        PATCH_REALTIME_USER(state, { realtime_user_id, realtime_user }) {
            if (state.playlist) {
                state.playlist = {
                    ...state.playlist,
                    realtime_user_id: realtime_user_id ?? null,
                    realtime_user: realtime_user ?? null,
                };
            }
        },
        APPLY_PLAYLIST_SNAPSHOT(state, { playlist }) {
            // Aplica snapshot vindo do transport (palco offline) preservando a posição
            // da música atual. O triple local da música visível (otimismo transpose) é
            // arbitrado depois pelo watcher (adopt com cooldown) — aqui só realoca.
            if (!playlist || !Array.isArray(playlist.songs)) return;
            const prev = state.currentSong;
            const prevIds = prev
                ? [prev.id, prev._id, prev.song_id, prev.songID, prev.song, prev.data?.id, prev.data?._id]
                    .filter(Boolean).map(String)
                : [];
            state.playlist = playlist;
            state.error = false;
            state.message = "";
            state.status = 200;
            if (prev && prevIds.length > 0) {
                const idx = playlist.songs.findIndex(s => {
                    const ids = [s.id, s._id, s.song_id, s.songID, s.song, s.data?.id, s.data?._id]
                        .filter(Boolean).map(String);
                    return ids.some(id => prevIds.includes(id));
                });
                if (idx > -1) {
                    state.currentSongPosition = idx;
                    state.currentSong = playlist.songs[idx];
                } else {
                    state.currentSongPosition = 0;
                    state.currentSong = playlist.songs[0] || null;
                }
            }
            try {
                const pid = playlist.id || playlist._id;
                if (pid) {
                    const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", pid);
                    if (window.cacheInstance) {
                        window.cacheInstance.setItem(playlistKey, playlist, playlistService.ttl);
                    }
                    dbService.setItem(playlistKey, playlist).catch(() => {});
                    if (playlistService.invalidateServiceWorkerCache) {
                        playlistService.invalidateServiceWorkerCache(pid);
                    }
                }
            } catch (e) { /* best-effort, ignore */ }
        },
        UPDATE_ALL(state, data){
            state.listPlaylists = data
        },
        UPDATE_ALL_OWNED_BY(state, data){
            state.playlistsOwnedBy = data
        },
        NEXT(state) {
            state.changedSong = false;
            if (!state.playlist || !state.playlist.songs || state.playlist.songs.length === 0) {
                state.currentSongPosition = 0;
            } else {
                // Ensure currentSongPosition is synced with currentSong before advancing
                if (state.currentSong) {
                    const currSongId = state.currentSong.song_id || state.currentSong.songID || state.currentSong.song || state.currentSong.data?.id || state.currentSong.data?._id || state.currentSong.id;
                    const realIdx = state.playlist.songs.findIndex(s => {
                        const sId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id || s.id;
                        return currSongId && sId && String(currSongId) === String(sId);
                    });
                    if (realIdx > -1) {
                        state.currentSongPosition = realIdx;
                    }
                }
                if (state.currentSongPosition + 1 < state.playlist.songs.length) {
                    state.currentSongPosition++;
                    state.changedSong = true;
                    state.currentSong = state.playlist.songs[state.currentSongPosition];
                }
            }
            const hasLyrics = state.currentSong && (state.currentSong.processed || state.currentSong.modifiedLyric || state.currentSong.data?.content || state.currentSong.data?.lyric);
            state.loading = !hasLyrics;
        },
        PREVIOUS(state) {
            state.changedSong = false;
            if (!state.playlist || !state.playlist.songs || state.playlist.songs.length === 0) {
                state.currentSongPosition = 0;
            } else {
                // Ensure currentSongPosition is synced with currentSong before going back
                if (state.currentSong) {
                    const currSongId = state.currentSong.song_id || state.currentSong.songID || state.currentSong.song || state.currentSong.data?.id || state.currentSong.data?._id || state.currentSong.id;
                    const realIdx = state.playlist.songs.findIndex(s => {
                        const sId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id || s.id;
                        return currSongId && sId && String(currSongId) === String(sId);
                    });
                    if (realIdx > -1) {
                        state.currentSongPosition = realIdx;
                    }
                }
                if (state.currentSongPosition > 0) {
                    state.currentSongPosition--;
                    state.changedSong = true;
                    state.currentSong = state.playlist.songs[state.currentSongPosition];
                }
            }
            const hasLyrics = state.currentSong && (state.currentSong.processed || state.currentSong.modifiedLyric || state.currentSong.data?.content || state.currentSong.data?.lyric);
            state.loading = !hasLyrics;
        },
        TRANSPOSE(state, payload) {
            // Resolve pelo songId (não pela posição): a posição atual pode ser de outra
            // música quando o transpose chega via realtime ou após navegação.
            let targetIdx = -1;
            const targetSongId = payload.songId || payload.data?.song_id || payload.data?.songID || payload.data?.song || payload.data?.id || payload.data?._id;
            if (state.playlist && state.playlist.songs && state.playlist.songs.length) {
                if (targetSongId) {
                    targetIdx = state.playlist.songs.findIndex(s => {
                        const sSongId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id;
                        const sId = s.id || s._id;
                        if (sSongId && sSongId === targetSongId) return true;
                        if (sId && sId === targetSongId) return true;
                        return false;
                    });
                }
            }
            if (targetIdx === -1) {
                targetIdx = state.currentSongPosition;
            }

            if (payload.data && payload.data.data && !payload.data.offline) {
                // If backend returns the full updated PlaylistSong
                state.playlist.songs[targetIdx] = payload.data;
            } else if (state.playlist && state.playlist.songs && state.playlist.songs[targetIdx]) {
                // Local or offline update
                let semitone = Number(payload.semitone);
                state.playlist.songs[targetIdx].chord = semitone;
            }

            if (state.playlist && state.playlist.songs && state.playlist.songs[targetIdx]) {
                state.currentSongPosition = targetIdx;
                state.currentSong = state.playlist.songs[targetIdx];
                const s = state.playlist.songs[targetIdx];
                if (s) {
                    const playlistId = state.playlist.id || state.playlist._id;
                    const sId = s.id || s._id || s.song_id || s.songID || s.song || s.data?.id || s.data?._id;
                    const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", playlistId);
                    if (window.cacheInstance) {
                        window.cacheInstance.setItem(playlistKey, state.playlist, playlistService.ttl);
                    }
                    dbService.setItem(playlistKey, state.playlist).catch(() => {});
                    if (sId) {
                        const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY
                            .replace("[id]", playlistId)
                            .replace("[songId]", sId);
                        if (window.cacheInstance) {
                            window.cacheInstance.setItem(songKey, s, playlistService.ttl);
                        }
                        dbService.setItem(songKey, s).catch(() => {});
                    }
                }
            }
        },
        LOADING(state, value){
            state.loading = !!value;
        },
        LOADING_OWNED_BY(state, value){
            state.loadingOwnedBy = value
        },
        DELETE(state) {
            state.playlist = null;
            state.error=false;
            state.message="Successfully deleted"
        },
        ERROR(state, result) {
            state.error = result.error
            state.message = result.message
        }
    },
    actions: {
        async loadAll(context) {
            context.commit("LOADING", true)
            let cachedData = window.cacheInstance ? window.cacheInstance.getItem(playlistService.ALL_PLAYLISTS_CACHE_KEY) : null
            if (!cachedData) {
                try {
                    cachedData = await dbService.getItem(playlistService.ALL_PLAYLISTS_CACHE_KEY);
                } catch (e) {
                    // Ignore cache read error
                }
            }
            if (cachedData) {
                context.commit("UPDATE_ALL", cachedData)
            }
            try{
                let data = await playlistService.fetchAll({force: true})
                context.commit("UPDATE_ALL", data)
                context.state.forceUpdate=false;
            }catch(e){
                console.warn(e)
                if (!cachedData) {
                    context.commit("UPDATE_ALL", [])
                }
            }
            context.commit("LOADING", false)
        },
        async loadByOwnedBy(context) {
            context.commit("LOADING_OWNED_BY", true)
            let cachedData = window.cacheInstance ? window.cacheInstance.getItem(playlistService.OWNED_BY_PLAYLISTS_CACHE_KEY) : null
            if (!cachedData) {
                try {
                    cachedData = await dbService.getItem(playlistService.OWNED_BY_PLAYLISTS_CACHE_KEY);
                } catch (e) {
                    // Ignore cache read error
                }
            }
            if (cachedData) {
                context.commit("UPDATE_ALL_OWNED_BY", cachedData)
            }
            try{
                let data = await playlistService.fetchOwnedBy({force: true})
                context.commit("UPDATE_ALL_OWNED_BY", data)
                context.state.forceUpdate=false;
            }catch(e){
                console.warn(e)
                if (!cachedData) {
                    context.commit("UPDATE_ALL_OWNED_BY", [])
                }
            }
            context.commit("LOADING_OWNED_BY", false)
        },
        async load(context, params) {
            let signal = null
            try{
                let error = false;
                const cacheKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", params.id)
                let cachedData = window.cacheInstance ? window.cacheInstance.getItem(cacheKey) : null
                if (!cachedData) {
                    try {
                        cachedData = await dbService.getItem(cacheKey);
                    } catch (e) {
                        // Ignore IndexedDB read error in cache lookup
                    }
                }

                const currentPId = context.state.playlist?.id || context.state.playlist?._id;
                const isDifferentPlaylist = !context.state.playlist || String(currentPId) !== String(params.id);
                if (isDifferentPlaylist) {
                    context.state.currentSong = null;
                    context.state.currentSongPosition = 0;
                    context.state.changedSong = false;
                }

                // SWR Instant Paint: If cached data exists in memory or IndexedDB, reveal it in 0ms!
                if (cachedData) {
                    context.commit("UPDATE", cachedData);
                    if (params.songId) {
                        await context.dispatch("loadSong", { ...params, useLoading: false, force: false });
                    } else {
                        context.commit("LOADING", false);
                    }
                } else {
                    context.commit("LOADING", true);
                    context.state.playlist = null;
                    context.state.currentSong = null;
                }

                const hasFullSongs = cachedData && Array.isArray(cachedData.songs) && cachedData.songs.length > 0 &&
                    cachedData.songs.every(s => (s.data?.title && s.data.title.trim() !== '') || (s.title && s.title.trim() !== '') || s.data?.content || s.data?.lyric || s.modifiedLyric);
                const needsRevalidation = !cachedData || !hasFullSongs || context.state.forceUpdate || params.force || true;
                if (needsRevalidation){
                    signal = abortSupersededRequest()
                    const data = await playlistService.fetchPlaylist({...params, force: true, signal})
                    if (signal.aborted) return
                    context.commit("UPDATE", data)
                    context.state.forceUpdate = false;
                    if (data.error){
                        error = true;
                    } else if (data.songs) {
                        prefetchPlaylistSongs(data.id || data._id, data.songs);
                    }
                    if (!error && params.songId) {
                        await context.dispatch("loadSong", {...params, useLoading: !hasFullSongs, force: false});
                    }
                }
            }catch(e){
                if (signal && signal.aborted) return
                console.warn(e)
                const cacheKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", params.id)
                const cachedData = window.cacheInstance ? window.cacheInstance.getItem(cacheKey) : null
                if (!cachedData) {
                    context.commit("UPDATE", null)
                }
            } finally {
                context.commit("LOADING", false)
            }
        },
        async loadSong(context, params) {
            /**
             * PLAYLIST_SONG MODEL
             */
            let signal = null
            try{
                if (context.state.playlist && context.state.playlist.songs){
                    const songIndex = context.state.playlist.songs.findIndex(s => {
                        const sSongId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id;
                        const sId = s.id || s._id;
                        return (sSongId && String(sSongId) === String(params.songId)) || (sId && String(sId) === String(params.songId));
                    });

                    const isFree = userStore.getters.isFree;
                    if (isFree && songIndex >= 10) {
                        const lockedSong = context.state.playlist.songs[songIndex];
                        context.commit("UPDATE_SONG", lockedSong);
                        context.commit("LOADING", false);
                        context.state.forceUpdate = false;
                        return;
                    }

                    const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY
                        .replace("[id]", context.state.playlist.id || context.state.playlist._id)
                        .replace("[songId]", params.songId);
                    
                    let cachedSong = window.cacheInstance ? window.cacheInstance.getItem(songKey) : null;
                    if (!cachedSong) {
                        try {
                            cachedSong = await dbService.getItem(songKey);
                        } catch (e) {
                            // Ignore IndexedDB read error in cache lookup
                        }
                    }
                    let data = null

                    if (songIndex > -1) {
                        data = context.state.playlist.songs[songIndex];
                    }

                    // Fresh playlist songs (already refetched from network by `load`)
                    // must win over the IndexedDB copy, otherwise Studio edits keep
                    // rendering the stale offline text. Cache only fills missing fields.
                    let cachedIsStale = false;
                    if (cachedSong) {
                        const cachedUpdatedAt = Date.parse(cachedSong?.updated_at || cachedSong?.data?.updated_at || 0) || 0;
                        const freshUpdatedAt = Date.parse(data?.updated_at || data?.data?.updated_at || 0) || 0;
                        cachedIsStale = freshUpdatedAt > cachedUpdatedAt;
                        data = cachedIsStale
                            ? { ...cachedSong, ...data }
                            : { ...(data || {}), ...cachedSong };
                    }

                    const hasProcessedLyrics = data && (data.processed || data.modifiedLyric || data.data?.content || data.data?.lyric);

                    // Render immediately if we have processed lyrics
                    if (hasProcessedLyrics) {
                        context.commit("UPDATE_SONG", data);
                        context.commit("LOADING", false);
                    } else {
                        // Song has no lyrics cached yet - keep loading state so skeleton displays
                        if (data) {
                            context.commit("UPDATE_SONG", data);
                        }
                        context.commit("LOADING", true);
                    }
                    // When online, always revalidate against the network: the cached
                    // render above is only the instant paint, not the source of truth.
                    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
                    const shouldFetchFromNetwork = !isOffline && (!hasProcessedLyrics || params.realtimeStatus || params.force || context.state.forceUpdate);

                    if (shouldFetchFromNetwork){
                        signal = abortSupersededRequest()
                        const epoch = ++context.state.songFetchEpoch;
                        const freshData = await playlistService.fetchSong({...params, force: true, signal})
                        if (signal.aborted) return
                        if (freshData && !freshData.error) {
                            try { freshData._fetchEpoch = epoch; } catch (e) { /* best-effort, ignore */ }
                            context.commit("UPDATE_SONG", freshData)
                        }
                    }
                }
                context.state.forceUpdate=false
            }catch(e){
                if (signal && signal.aborted) return
                console.warn(e)
            } finally {
                context.commit("LOADING", false)
            }
        },
        async delete(context, id) {
            const result = await playlistService.deletePlaylist(id)
            const data = await result.json()
            if (result.status==200){
                if (data.error){
                    context.commit("ERROR", data);
                } else {
                    playlistService.removeCachedPlaylist(id)
                    playlistService.clearCache(playlistService.ALL_PLAYLISTS_CACHE_KEY)
                    playlistService.clearCache(playlistService.OWNED_BY_PLAYLISTS_CACHE_KEY)
                    const { default: router } = await import('@/router');
                    router.push({name: "Dashboard"})
                    context.commit("DELETE");
                }
            } else {
                context.commit("ERROR", data);
            }
        },
        addSong(context, params){
            return songService.add(params.playlist.id || params.playlist._id, params.song.id || params.song._id).then( async (response) => {
                const data = await response.json();
                if (!response.ok) {
                    if (data && (data.error === 'LIMIT_REACHED' || data.message === 'LIMIT_REACHED')) {
                        const err = new Error('LIMIT_REACHED');
                        err.isLimitReached = true;
                        throw err;
                    }
                    throw new Error(data.message || 'Error adding song');
                }
                context.state.forceUpdate=true;
                playlistService.clearCache(playlistService.PLAYLIST_CACHE_KEY.replace("[id]", params.playlist.id || params.playlist._id))
                playlistService.clearCache(playlistService.ALL_PLAYLISTS_CACHE_KEY)
                playlistService.clearCache(playlistService.OWNED_BY_PLAYLISTS_CACHE_KEY)
            }).catch((e) =>{
                if (!e.isLimitReached) {
                    context.commit("ERROR", e);
                }
                throw e;
            })
        },
        removeSong(context, params){
            return songService.removeSong(params.playlist.id || params.playlist._id, params.song.id || params.song._id).then( () => {
                context.state.forceUpdate=true;
                playlistService.clearCache(playlistService.PLAYLIST_CACHE_KEY.replace("[id]", params.playlist.id || params.playlist._id))
                playlistService.clearCache(playlistService.ALL_PLAYLISTS_CACHE_KEY)
                playlistService.clearCache(playlistService.OWNED_BY_PLAYLISTS_CACHE_KEY)
            }).catch((e) =>{
                context.commit("ERROR", e);
                throw e;
            })
        },
        update(context, id) {
            context.dispatch("load", {id});
        },
        next(context) {
            let currentPos = context.state.currentSongPosition;
            if (context.state.currentSong && context.state.playlist?.songs?.length) {
                const currSongId = context.state.currentSong.song_id || context.state.currentSong.songID || context.state.currentSong.song || context.state.currentSong.data?.id || context.state.currentSong.data?._id || context.state.currentSong.id;
                const realIdx = context.state.playlist.songs.findIndex(s => {
                    const sId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id || s.id;
                    return currSongId && sId && String(currSongId) === String(sId);
                });
                if (realIdx > -1) {
                    currentPos = realIdx;
                }
            }
            if (!canNavigateSong(context.state.playlist?.songs, currentPos, 1)) {
                return context.state.currentSong;
            }
            context.commit("NEXT")
            context.commit("UPDATE_CURRENT_SONG")
            return context.state.currentSong
        },
        previous(context) {
            let currentPos = context.state.currentSongPosition;
            if (context.state.currentSong && context.state.playlist?.songs?.length) {
                const currSongId = context.state.currentSong.song_id || context.state.currentSong.songID || context.state.currentSong.song || context.state.currentSong.data?.id || context.state.currentSong.data?._id || context.state.currentSong.id;
                const realIdx = context.state.playlist.songs.findIndex(s => {
                    const sId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id || s.id;
                    return currSongId && sId && String(currSongId) === String(sId);
                });
                if (realIdx > -1) {
                    currentPos = realIdx;
                }
            }
            if (!canNavigateSong(context.state.playlist?.songs, currentPos, -1)) {
                return context.state.currentSong;
            }
            context.commit("PREVIOUS")
            context.commit("UPDATE_CURRENT_SONG")
            return context.state.currentSong
        },
        transpose(context, params){
            /**
             * SONG MODEL
             * realtimeStatus repassado explicitamente conforme o switch do usuário.
             */
            let url = '/api/playlist/'+params.id+'/song/transpose/'+params.songId+"?chord="+params.current+"&semitone="+params.semitone;
            if (params.realtimeStatus === true || params.realtimeStatus === 'true') {
                url += "&realtimeStatus=true";
            } else {
                url += "&realtimeStatus=false";
            }
            // GET com efeito colateral (salva + broadcast opcional): nunca pode vir do SW
            // (StaleWhileRevalidate servia resposta stale e o reconcile revertia o otimismo).
            return fetch(url, { cache: 'no-store' })
            .catch(console.warn)
            .then(async (response) => {
                let data = await response.json()
                if (data && typeof data === 'object') {
                    try { data._fetchEpoch = ++context.state.songFetchEpoch; } catch (e) { /* best-effort, ignore */ }
                }
                context.commit("TRANSPOSE", {data, semitone: params.semitone, songId: params.songId})
                return data
            })
        },
        clean(context) {
            context.state.playlist = null;
            context.state.forceUpdate = false;
            context.state.currentSong = null;
            context.state.currentSongPosition = 0;
            context.state.changedSong = false;
            context.state.loading = false;
            context.state.error = false;
            context.state.message = "";
            context.state.status = 200;
        }
    }
})

export default store;